import { useEffect } from 'react';
import { isAuthenticated } from '../util/amexSession';

// Silently send the user to a logout URL this many ms after the page renders.
export const AUTO_LOGOUT_DELAY_MS = 10000;

// A logout URL is any href that routes through the identity /logout endpoint.
export const isLogoutUrl = (href) => typeof href === 'string' && href.includes('/logout');

/**
 * When `href` is a logout URL AND the user has an authenticated AmexSession,
 * silently navigate to it after AUTO_LOGOUT_DELAY_MS (no countdown UI).
 *
 * This distinguishes the two journeys that share these result pages:
 *  - App-load / login flow — the user is authenticated, so a "please log in
 *    again" success/error page silently logs them out after 10 seconds.
 *  - Sign-up flow — the user is creating a new account and is not yet
 *    authenticated, so the success / "Almost There" pages never auto-logout.
 *
 * No-op for non-logout hrefs or when there is no authenticated session.
 */
const useAutoLogout = (href) => {
  useEffect(() => {
    if (!isLogoutUrl(href) || !isAuthenticated()) return undefined;

    const timer = setTimeout(() => {
      window.location.assign(href);
    }, AUTO_LOGOUT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [href]);
};

export default useAutoLogout;
